import { createContext, useCallback, useContext, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  AlertModal, ConfirmModal, InputModal,
  type AlertModalProps, type ConfirmModalProps, type InputModalProps,
} from "@/components/ui/PromptModals";

/**
 * Imperative replacement for the browser-native window.confirm /
 * window.prompt / window.alert. Exposes promise-returning methods so
 * migration from native dialogs stays a minimal edit:
 *
 *   const yes = await dialogs.confirm({ title: "Delete?", ... });
 *   const name = await dialogs.prompt({ title: "Territory name", ... });
 *   await dialogs.alert({ title: "Done", ... });
 *
 * Backed by a single <DialogsProvider> at the app root that renders the
 * underlying modal components via useState, so there's only ever one
 * open at a time and the DOM stays tidy.
 */

type ConfirmOptions = Omit<ConfirmModalProps, "open" | "onConfirm" | "onCancel">;
type PromptOptions = Omit<InputModalProps, "open" | "onSubmit" | "onCancel">;
type AlertOptions = Omit<AlertModalProps, "open" | "onClose">;

interface DialogsContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  prompt: (opts: PromptOptions) => Promise<string | null>;
  alert: (opts: AlertOptions) => Promise<void>;
}

const DialogsContext = createContext<DialogsContextValue | undefined>(undefined);

type Pending =
  | { kind: "confirm"; options: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: "prompt"; options: PromptOptions; resolve: (v: string | null) => void }
  | { kind: "alert"; options: AlertOptions; resolve: () => void };

export function DialogsProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const resolveRef = useRef<Pending["resolve"] | null>(null);
  resolveRef.current = pending?.resolve ?? null;

  const confirm = useCallback((options: ConfirmOptions) => new Promise<boolean>((resolve) => {
    setPending({ kind: "confirm", options, resolve });
  }), []);

  const prompt = useCallback((options: PromptOptions) => new Promise<string | null>((resolve) => {
    setPending({ kind: "prompt", options, resolve });
  }), []);

  const alert = useCallback((options: AlertOptions) => new Promise<void>((resolve) => {
    setPending({ kind: "alert", options, resolve });
  }), []);

  return (
    <DialogsContext.Provider value={{ confirm, prompt, alert }}>
      {children}

      {pending?.kind === "confirm" && (
        <ConfirmModal
          open
          {...pending.options}
          onConfirm={() => { pending.resolve(true); setPending(null); }}
          onCancel={() => { pending.resolve(false); setPending(null); }}
        />
      )}

      {pending?.kind === "prompt" && (
        <InputModal
          open
          {...pending.options}
          onSubmit={(v) => { pending.resolve(v); setPending(null); }}
          onCancel={() => { pending.resolve(null); setPending(null); }}
        />
      )}

      {pending?.kind === "alert" && (
        <AlertModal
          open
          {...pending.options}
          onClose={() => { pending.resolve(); setPending(null); }}
        />
      )}
    </DialogsContext.Provider>
  );
}

/** Access dialogs from inside a component — mirrors the API of
 *  window.confirm / window.prompt / window.alert but promise-based. */
export function useDialogs(): DialogsContextValue {
  const ctx = useContext(DialogsContext);
  if (!ctx) throw new Error("useDialogs must be used inside <DialogsProvider>.");
  return ctx;
}
